import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button, Icons, iconSize, palette, Stack, Typography } from '@munifocus/ui';
import { useOpenFileThread } from '../../lib/chat-api';

/**
 * The way into a file's discussion thread.
 *
 * Opening is a mutation, not a link, because the thread may not exist yet — it
 * is created on first use and seeded with whoever is on the file. Making the
 * officer create it explicitly would be asking them to answer a question they
 * have no way to have an opinion about.
 *
 * The notice under the button is not decoration. A file thread is part of the
 * enforcement record and is subject to disclosure with it, which is a materially different
 * thing from a direct message to a colleague — and the only moment that is worth
 * saying is before someone types into it.
 */
export function FileDiscussionPanel({ fileId }: { fileId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const openThread = useOpenFileThread();

  return (
    <Stack sx={{ gap: 1, alignItems: 'flex-start' }}>
      <Typography variant="h6">{t('common.chat.fileThread')}</Typography>

      <Typography variant="body2" sx={{ color: palette.text.muted }}>
        {t('common.chat.fileThreadHint')}
      </Typography>

      <Button
        variant="outlined"
        startIcon={<Icons.MessageSquare size={iconSize.md} aria-hidden />}
        disabled={openThread.isPending}
        onClick={() =>
          openThread.mutate(fileId, {
            onSuccess: (conversation) => navigate(`/messages?conversation=${conversation.id}`),
          })
        }
        sx={{ mt: 0.5 }}
      >
        {t('common.chat.openFileThread')}
      </Button>
    </Stack>
  );
}
